import React from "react"
import { graphql, Link } from "gatsby"
import {
  SiFlutter,
  SiAndroid,
  SiSwift,
  SiFirebase,
  SiSqlite,
  SiAndroidstudio,
  SiXcode,
  SiAppstore,
  SiGoogleplay,
  SiAmazonaws,
  SiMicrosoftazure,
  SiLinux,
  SiNginx,
  SiApache,
  SiDocker,
} from "react-icons/si"

import Layout from "../../components/Layout"
import ServiceProof from "../../components/ServiceProof"
import ServiceTechStack from "../../components/ServiceTechStack"
import { getOffer } from "../../data/offers"

import "../../styles/servicePage.scss"

/**
 * /services/mobiledevelopment/ — the mobile practice.
 *
 * Rebuilt to the /services/aisolution/ template, same as
 * /services/webdevelopment/: nine sections in the given order on the .sp-
 * system, plus the technology stack after section four.
 *
 * INDEXED. Frozen and byte-identical to git HEAD: the <h1>, the <title>, the
 * meta description, the canonical and the URL.
 *
 * ONE h1. The previous page rendered four — antd's Typography.Title defaults to
 * h1, and the hero, "What We Can Build For You", the process heading and the
 * stack heading all used it.
 *
 * THE CHEVRON IS GONE FROM THIS PAGE. It was six steps here against five on the
 * web page, but four of the five shared bodies were verbatim apart from a
 * trailing full stop, and both pages carried the identical section title — so it
 * was never really about mobile work. Section 5 replaces it. The shared
 * ServiceStepsProcess and WEB_DevelopmentProcess components are untouched,
 * because /services/staffaugmentation/ still renders them and is not in scope.
 *
 * NO UMRAH SHUTTLE PROOF. The brief asked for it if a page exists; it does not.
 * The app appears in MOB_FeaturedWork.js as a panel whose "Explore More" <Link>
 * carries no `to` prop at all, and there is no route for it anywhere in the
 * build. Big Jerry's Fencing is used instead — a fencing installer running crews
 * across 21 cities whose engagement included, in the case study's own words, an
 * "Installer Mobile App". Its field metrics are quoted here; the AI metrics from
 * the same study belong to /services/aisolution/ and are not repeated.
 *
 * Prices appear in ONE place: the offer rows in section 6.
 */

const OFFER_SLUGS = [
  "field-service-installer-platform",
  "managed-living-platform",
  "ai-voice-receptionist",
]

// Resolved from the catalogue so a price, a timeline or a slug can never drift
// out of sync with the offer page it points at.
const PRACTICE_OFFERS = OFFER_SLUGS.map(getOffer).filter(Boolean)

/* 2. The three audiences the brief named — installation and field-service firms
   with crews in vans, residential and building operators, and businesses whose
   customers phone rather than log in — two problems each. */
const PROBLEMS = [
  "Your crews leave in the morning with a paper list and you find out what actually happened when they get back.",
  "Proof that a job was done properly is a photo on somebody's personal phone, and nobody can find it when the customer disputes the invoice.",
  "Residents and tenants request everything by message, so the front desk is a queue and nothing is logged.",
  "The same maintenance issue gets reported three times because there is nowhere for anyone to see it was already reported once.",
  "Your customers phone rather than log in, and outside office hours that call goes to voicemail.",
  "The signal drops on half the sites your teams visit, and whatever they were filling in drops with it.",
]

/* 4. What the practice actually builds. */
const CAPABILITIES = [
  {
    title: "Apps that keep working when the signal does not",
    body:
      "A job list, a form and a camera that carry on in a basement, a plant room or a field with one bar. Work is written to the device first and synced when the connection returns, so a crew is never blocked and nothing is retyped in the van afterwards.",
    stack: ["Flutter", "SQLite", "Offline-first", "Background sync"],
  },
  {
    title: "Proof of work that lands where the invoice is raised",
    body:
      "Photographs, signatures, timestamps and a note against the right job, filed the moment they are captured. When a customer disputes what was done, the answer is in the record rather than on somebody's personal phone.",
    stack: ["Camera", "Geolocation", "Firebase", "Audit trail"],
  },
  {
    title: "Apps wired into the systems that already hold your records",
    body:
      "A siloed app is one more place to look. Yours connects to your enterprise resource planning system, your customer relationship management tool, your payment gateway and whatever third-party services you depend on, through secure interfaces built for the load rather than for the demo.",
    stack: ["API", "ERP", "CRM", "Payment gateways", "Third-party services"],
  },
  {
    title: "Interfaces people do not abandon in the first minute",
    body:
      "Most users abandon an app within seconds of deciding it is hard, and app store ratings remember it. Wireframing and prototyping happen before code, and the UI/UX is tested with the people who will actually use it — in gloves, in daylight, one-handed — so friction is designed out rather than reported later. First-week churn is the number that tells you whether an app retains anybody.",
    stack: ["UI/UX", "Wireframing", "Prototyping", "Churn", "Accessibility"],
  },
  {
    title: "Resident and customer apps that answer for themselves",
    body:
      "The requests, bookings, payments and updates your front desk currently fields, handled by the person making them. Fewer calls to answer, and a log of what was asked that nobody has to keep by hand.",
    stack: ["Self-service", "Notifications", "Payments", "Messaging"],
  },
  {
    title: "Intelligence where a prediction changes something",
    body:
      "Smart search, personalised recommendations and predictive prompts belong in the places where they change what someone does next: the part likely needed on this job, the request about to breach its window. Built on your own history, and left out where a rule would do the same work.",
    stack: ["AI", "Smart search", "Recommendations", "Analytics"],
  },
]

/* 5. Named phases, no numbers and no dates. */
const PHASES = [
  {
    phase: "Before anything is built",
    title: "Where the work actually happens",
    body:
      "You get an honest read on the conditions the app has to survive: which sites lose signal, what a crew is holding while they use it, and which of your systems it has to talk to. The architecture follows from that rather than from a preference.",
  },
  {
    phase: "Agreeing the shape",
    title: "Scope written down, then held",
    body:
      "Scope is agreed in writing before work starts, and change is handled explicitly: anything new is quoted as an addition rather than absorbed quietly, so the thing you approved stays the thing being built.",
  },
  {
    phase: "While it is being built",
    title: "A build on your own device every cycle",
    body:
      "Work is pulled from a backlog you can see and lands in short sprints as an installable build you run on your own phone, on your own network. Testing includes the awkward cases — no signal, low battery, an older handset — because that is where field apps fail.",
  },
  {
    phase: "Getting it into your users' hands",
    title: "Through review, without a scramble",
    body:
      "Store submission is part of the plan rather than a surprise at the end. Deployment to the Apple App Store and Google Play is prepared with the metadata, permissions and privacy answers those reviews demand, so a rejection is a correction rather than a delay.",
  },
  {
    phase: "After it is live",
    title: "Watched, supported, and yours",
    body:
      "Crash reporting and monitoring sit on the running app, and post-launch support covers the operating system updates that arrive whether you are ready or not. Your code, your signing keys and your store listings are yours — you can move them to another team.",
  },
]

/* 7. Proof from this discipline only. Big Jerry's Fencing runs installation
   crews and its build included an Installer Mobile App, which is exactly this
   practice. Route verified live, not inferred from a filename.

   THE FIGURES ARE THE CLIENT'S AND ARE QUOTED, NOT REWRITTEN. The two metrics
   /services/aisolution/ uses from this same study are deliberately not repeated
   here. */
const PROOF = {
  client: "Big Jerry's Fencing",
  path: "/caseStudies/projects/bigJerrysFencing",
  metrics: [
    "40% faster turnaround from lead to completed installation",
    // "25% higher quote-to-win ratio with Good / Better / Best quoting options"
    // WAS HERE AND HAS BEEN REMOVED. It is Big Jerry's QUOTING outcome — the
    // metric names its own cause in its own label, and that cause is the
    // Good/Better/Best tiering in the CRM, not the installer app. It belongs on
    // /offers/field-service-installer-platform/, where it already sits, because
    // that offer sells the quoting configuration. Leaving it here made a
    // quoting result read as a mobile result.
    //
    // This page's own header already applied the discipline test to the AI
    // metrics from the same case study ("the AI metrics from the same study
    // belong to /services/aisolution/ and are not repeated"). The same test was
    // simply never applied to this one.
    //
    // NO NUMERAL EXISTS IN THIS ONE and none is invented. ServiceProof renders
    // it below the stat row rather than under an empty numeral slot.
    "Improved installer productivity with clear tasks and mobile proof-of-work",
  ],
}

/* 8. Questions about the discipline, not about any one offer. */
const FAQS = [
  {
    q: "Do we need one app or two — iOS and Android?",
    a: "One codebase, both platforms, unless there is a specific reason not to. That is what section three is about: a cross-platform build gives you a single thing to pay for and maintain, and it is the right answer for most business apps. Where an app leans hard on the camera, on background location or on something a platform has only just shipped, native is worth the second bill.",
  },
  {
    q: "Our crews work where there is no signal. Is that a problem?",
    a: "It is the normal case rather than the exception, and it shapes the build. Work is written to the device first and synced when a connection returns, which means designing for what happens when two people edited the same job while both were offline. Assume any app that needs a live connection to save will lose your crews' work.",
  },
  {
    q: "Do we have to replace the systems we already run?",
    a: "No. The records you keep in an enterprise resource planning or customer relationship management system stay where they are, and the app reads and writes through the interfaces they already expose. An app that duplicates your data instead of connecting to it becomes a second version of the truth.",
  },
  {
    q: "Who owns the code, and the app store listings?",
    a: "You do — the repositories, the signing keys, the store accounts and the documentation. Publishing under your own developer account rather than someone else's is worth insisting on with any supplier, because moving a live app off another company's account later is genuinely painful.",
  },
  {
    q: "What about the app we already have?",
    a: "A legacy app can usually be modernised rather than rebuilt: the parts your users depend on stay, the architecture underneath them changes, and the ratings you have built up survive. Whether that is cheaper than starting again depends on how it was written, and that is a question worth answering before either bill is quoted.",
  },
  {
    q: "How long does this take, and what does it cost?",
    a: "Open-ended work is scoped to your situation and quoted against that scope, which is why no duration or figure is published for it here. Three builds in this practice have been delivered often enough to state both upfront, and those are listed above — start there if a date you can hold someone to matters more than an exact fit.",
  },
]

/* 4b. The mobile technology stack, kept in full. Both icon and name render, so
   these are terms in the copy rather than logos a reader has to recognise. */
const STACK_GROUPS = [
  {
    label: "Languages and frameworks",
    items: [
      { node: <SiFlutter />, name: "Flutter" },
      { node: <SiSwift />, name: "Swift" },
      { node: <SiAndroid />, name: "Android" },
    ],
  },
  {
    label: "On-device and hosted data",
    items: [
      { node: <SiSqlite />, name: "SQLite" },
      { node: <SiFirebase />, name: "Firebase" },
    ],
  },
  {
    label: "Build and release",
    items: [
      { node: <SiAndroidstudio />, name: "Android Studio" },
      { node: <SiXcode />, name: "Xcode" },
      { node: <SiAppstore />, name: "Apple App Store" },
      { node: <SiGoogleplay />, name: "Google Play" },
    ],
  },
  {
    label: "Back-end infrastructure",
    items: [
      { node: <SiAmazonaws />, name: "AWS" },
      { node: <SiMicrosoftazure />, name: "Microsoft Azure" },
      { node: <SiLinux />, name: "Linux" },
      { node: <SiNginx />, name: "Nginx" },
      { node: <SiApache />, name: "Apache" },
      { node: <SiDocker />, name: "Docker" },
    ],
  },
]

/* Rewritten to second person. The previous three paragraphs opened "We use",
   "Databases are used" and "When the application is developed", and two of them
   carried typos that dropped a product name: "Microsofta zure", and "Nginx,
   Nginx and Docker" where Apache should have been. Both are named correctly
   here. */
const STACK_PROSE = [
  "Your app is built in Flutter unless something in it argues for going native, in which case that part is written in Swift for iOS or against the Android platform directly. One codebase is cheaper to own and faster to change, and the reason to give that up should be a feature you need rather than a preference somebody has.",
  "Data sits in two places for a reason. SQLite on the device is what lets a crew carry on with no signal, because the app writes locally first; Firebase handles the sync, the push notifications and the account sign-in once a connection is back. Which records live where is decided by what has to survive a dead zone.",
  "Behind the app, AWS — Amazon Web Services — or Microsoft Azure, with Linux, Nginx, Apache and Docker, gives you somewhere to scale as your user base grows and somewhere you could move away from. Android Studio and Xcode are where the builds are produced, and the Apple App Store and Google Play are treated as part of the plan rather than the last hurdle.",
]

const MobileDevelopment = ({ data }) => (
  <Layout
    className="services-page-container"
    data={data}
    // FROZEN — byte-identical to git HEAD. Do not touch.
    pageTitle={"Visiomate - Mobile Development Services"}
    descrip={
      "Bring your mobile app ideas to life. Our expert mobile development services deliver high-quality, user-friendly apps for iOS and Android. Contact us today!"
    }
  >
    <article className="sp">
      {/* ── 1. HERO — FULL WIDTH, no split, no rail ─────────────────────────── */}
      <section className="sp-hero">
        <div className="sp-hero__inner">
          <span className="sp-tag">Mobile practice</span>
          {/* FROZEN h1. The only h1 on the page. */}
          <h1 className="sp-hero__h1">
            AI-Powered Mobile Apps for iOS &amp; Android
          </h1>
          <p className="sp-hero__lede">
            Your work happens away from a desk — in a van, on a site, at a front
            desk, on a phone call you did not get to answer. What you do not have
            is that work landing in your systems as it happens rather than hours
            later. That is the gap this practice closes: apps built for the
            conditions your people actually work in, wired to the records you
            already keep.
          </p>
          <Link to="/contact" className="sp-hero__cta">
            Talk through what you are dealing with
          </Link>
        </div>
      </section>

      {/* ── 2. PROBLEMS — two columns, hairlines, no numbers, no cards ──────── */}
      <section className="sp-problems">
        <div className="sp-problems__inner">
          <span className="sp-tag">Where this starts</span>
          <h2 className="sp-h2">The problems this practice solves</h2>
          <p className="sp-lede">
            None of these are app problems to begin with. They are
            what-happened-out-there problems, and an app is only the answer
            because that is what your people are already holding.
          </p>
          <ul className="sp-problems__grid">
            {PROBLEMS.map(item => (
              <li className="sp-problems__item" key={item}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── 3. WHAT THIS ACTUALLY IS — plain language, no bare acronyms ─────── */}
      <section className="sp-what">
        <div className="sp-what__inner">
          {/* FIX 1 — two columns. This ran 300+ words in one narrow left
              column with the right half of the page empty. The heading and a
              standfirst carry the point on the left; the argument is broken
              into its own divisions on the right, at a 34rem measure. */}
          <div className="sp-what__cols">
            <div className="sp-what__lead">
              <span className="sp-tag">In plain terms</span>
              <h2 className="sp-h2">What this actually is</h2>
              <p className="sp-what__standfirst">
                The platform question matters less than the two that follow it: whether
                the app keeps working with no signal, and whether it writes into the
                systems you already run. Those decide whether your people use it at all.
              </p>
            </div>
            <div className="sp-what__body">
            <div className="sp-what__part">
              <h3 className="sp-what__parttitle">Native versus cross-platform</h3>
              <p>
                Two words get used as though the choice were obvious. A native app is
                written separately for each platform, in the language that platform
                expects, which means two codebases and two bills but the closest
                possible access to the device. A cross-platform app is written once and
                runs on both. For most business apps the second is the right answer,
                because your users cannot tell and you only maintain one thing. Native
                earns its cost where an app leans hard on the camera, on background
                location, or on a capability a platform shipped last month.
              </p>
            </div>
            <div className="sp-what__part">
              <h3 className="sp-what__parttitle">Why offline capability decides it</h3>
              <p>
                What matters far more, if your people work away from a desk, is whether
                the app can carry on with no signal. Most apps assume a connection: the
                form saves to a server, so no connection means no save. A crew in a
                plant room or on a rural site loses whatever they had entered, discovers
                it later, and stops trusting the app — which is how you end up back on
                paper. Building offline-first means the work is written on the phone
                first and sent when the signal returns, which sounds small and changes
                the whole architecture, because the app now has to decide what happens
                when two people changed the same job while both were out of range.
              </p>
            </div>
            <div className="sp-what__part">
              <h3 className="sp-what__parttitle">What integrating with an existing system involves</h3>
              <p>
                Then there is integration, which is the word that gets explained least.
                Connecting an app to a system you already run means agreeing what each
                side owns, translating between two different ideas of what a job or a
                customer is, and deciding what happens when the older system is down or
                slow. It is not a switch. Done properly, a crew marks a job complete and
                the invoice is ready without anyone retyping it. Skipped, the app
                becomes a second place to enter the same information — which is worse
                than not having it.
              </p>
            </div>
            </div>
          </div>
          <p className="sp-what__pull">
            The useful question is never which platform to build for. It is what
            your people currently write down to type in later, and what it costs you
            between the writing and the typing.
          </p>
        </div>
      </section>

      {/* ── 4. WHAT GETS BUILT WITH IT — THE DARK BAND, cards ───────────────── */}
      <section className="sp-build">
        <div className="sp-build__inner">
          <span className="sp-tag">The capabilities</span>
          <h2 className="sp-h2">What gets built with it</h2>
          <p className="sp-lede">
            Six things account for nearly every engagement in this practice.
            Most projects are two or three of them wired together, not all six.
          </p>
          <div className="sp-build__grid">
            {CAPABILITIES.map(cap => (
              <div className="sp-build__card" key={cap.title}>
                <h3 className="sp-build__cardtitle">{cap.title}</h3>
                <p className="sp-build__cardbody">{cap.body}</p>
                <ul className="sp-build__stack">
                  {cap.stack.map(tech => (
                    <li className="sp-build__stackitem" key={tech}>
                      {tech}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4b. TECHNOLOGY STACK — kept, moved here, rewritten to second person */}
      <ServiceTechStack
        eyebrow="What it is built on"
        heading="The technology stack behind it"
        lede="You inherit these choices for years, so they are worth seeing before you commit rather than discovering afterwards."
        groups={STACK_GROUPS}
        paragraphs={STACK_PROSE}
      />

      {/* ── 5. HOW WE WORK — named phases, no week numbers, no dates ────────── */}
      <section className="sp-how">
        <div className="sp-how__inner">
          <span className="sp-tag">How the work runs</span>
          <h2 className="sp-h2">How open-ended work is run</h2>
          <p className="sp-lede">
            Work in this practice rarely arrives with a date attached, so what
            you get instead of a delivery date is visibility: agreed scope,
            something to install every cycle, and a way out.
          </p>
          <ol className="sp-how__steps">
            {PHASES.map(ph => (
              <li className="sp-how__step" key={ph.title}>
                <span className="sp-how__marker" aria-hidden="true" />
                <span className="sp-how__phase">{ph.phase}</span>
                <h3 className="sp-how__title">{ph.title}</h3>
                <p className="sp-how__body">{ph.body}</p>
              </li>
            ))}
          </ol>
          <p className="sp-how__note">
            No date is published for work of this shape, and none is implied. If
            what you need is a scope and a date you can hold someone to, the
            packages below carry both.
          </p>
        </div>
      </section>

      {/* ── 6. FIXED-SCOPE OPTIONS — rows, prices shown ─────────────────────── */}
      <section className="sp-offers">
        <div className="sp-offers__inner">
          <span className="sp-tag">Fixed-scope options</span>
          <h2 className="sp-h2">Some of this work has a written scope already</h2>
          <p className="sp-lede">
            Three builds in this practice have been delivered enough times to
            state upfront what they contain, how long they take and what they
            cost.
          </p>
          <ul className="sp-offers__list">
            {PRACTICE_OFFERS.map(o => (
              <li className="sp-offers__item" key={o.slug}>
                <Link to={`/offers/${o.slug}`} className="sp-offers__row">
                  <span className="sp-offers__main">
                    <span className="sp-offers__name">{o.name}</span>
                    <span className="sp-offers__problem">{o.symptoms[0]}</span>
                  </span>
                  <span className="sp-offers__meta">
                    <span className="sp-offers__timeline">
                      {o.timelineShort}
                    </span>
                    <span className="sp-offers__price">{o.priceShort}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <p className="sp-offers__all">
            <Link to="/offers">Every offer, across every practice &rarr;</Link>
          </p>
        </div>
      </section>

      {/* ── 7. PROOF — the shared component. FIX 2: every metric leads
             with its figure at $fs-stat, a CLIENT tag carries the attribution,
             and the name links to the case study because that route resolves.
             components/ServiceProof.js ─────────────────────────────────────── */}
      <ServiceProof
        client={PROOF.client}
        path={PROOF.path}
        metrics={PROOF.metrics}
      />

      {/* ── 8. QUESTIONS — about the discipline ─────────────────────────────── */}
      <section className="sp-faq">
        <div className="sp-faq__inner">
          <span className="sp-tag">Questions</span>
          <h2 className="sp-h2">What buyers ask about mobile work</h2>
          <ul className="sp-faq__list">
            {FAQS.map(f => (
              <li className="sp-faq__item" key={f.q}>
                <p className="sp-faq__q">{f.q}</p>
                <p className="sp-faq__a">{f.a}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── 9. IF YOUR NEED IS BROADER — one CTA ────────────────────────────── */}
      <section className="sp-broader">
        <div className="sp-broader__inner">
          <span className="sp-tag">If your need is broader</span>
          <h2 className="sp-h2">If none of that is quite your situation</h2>
          <div className="sp-broader__cols">
            <p className="sp-broader__text">
              Plenty of what lands in this practice does not fit a package: a
              legacy app that needs modernising without losing the ratings it
              has earned, a device or piece of hardware the app has to talk to,
              an app that has to satisfy a regulator, or engineers working
              alongside your own team on a roadmap you already have.
            </p>
            <p className="sp-broader__text">
              Describe what you are dealing with in a free consultancy session
              and you will get a straight read on how it would be approached,
              what it depends on, and where the risk sits. If the answer is that
              you do not need an app at all, that is what you will hear.
            </p>
          </div>
          <div className="sp-broader__foot">
            <Link to="/contact" className="sp-broader__cta">
              Tell us what you are dealing with
            </Link>
          </div>
        </div>
      </section>
    </article>
  </Layout>
)

export default MobileDevelopment

export const mobileDevelopmentQuery = graphql`
  query mobileDevelopmentQuery {
    bgImage: file(relativePath: { eq: "Mask_group8.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
